import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../Style/CreateAssignRole.css";
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Home,
  User,
  Users,
  Briefcase,
  Calendar,
  FileUp,
  Save,
  Search,
  ChevronDown,
  CheckCircle2
} from "lucide-react";

const SearchableUnitSelect = ({ allUnitNumber = [], value, onChange, placeholder = "Select Unit" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const options = allUnitNumber
    .map((item) => {
      const memberName = item?.member_name || "";
      const unitNo = item?.unit_no || "";
      const wing = item?.wing || "";
      return unitNo ? { value: unitNo, label: memberName, wing } : null;
    })
    .filter(Boolean);

  const selectedOpt = options.find((o) => String(o.value) === String(value));
  const filtered = query.trim()
    ? options.filter(
      (o) =>
        o.value.toString().toLowerCase().includes(query.toLowerCase()) ||
        (o.label && o.label.toLowerCase().includes(query.toLowerCase()))
    )
    : options;

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const choose = (opt) => {
    setOpen(false);
    setQuery("");
    onChange(opt);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0) choose(filtered[0]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div ref={containerRef} className="searchable-select-modern">
      <div className="select-input-wrapper">
        <Search size={16} className="select-icon" />
        <input
          type="text"
          placeholder={placeholder}
          value={open ? query : selectedOpt ? `${selectedOpt.value} — ${selectedOpt.label}` : ""}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-expanded={open}
          aria-haspopup="listbox"
          className="select-input"
        />
        <button
          type="button"
          onClick={() => {
            setOpen((v) => !v);
            setQuery("");
          }}
          className="select-toggle-btn"
          aria-label="toggle"
        >
          <ChevronDown size={16} className={open ? 'rotated' : ''} />
        </button>
      </div>

      {open && (
        <div role="listbox" tabIndex={-1} className="select-dropdown">
          {filtered.length === 0 ? (
            <div className="select-empty">No results</div>
          ) : (
            filtered.map((opt, idx) => (
              <div
                key={idx}
                role="option"
                onClick={() => choose(opt)}
                onKeyDown={(e) => e.key === "Enter" && choose(opt)}
                className="select-option"
              >
                <strong>{opt.value}</strong> — <span>{opt.label}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const CreateAssignRole = () => {
  const [allUnitNumber, setAllUnitNumber] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const societyName = (localStorage.getItem("society") || "").trim();

  const [formData, setFormData] = useState({
    unitNo: "",
    name: "",
    memberName: "",
    designation: "",
    startDate: "",
    agree: false,
    wing: "",
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.unitNo) newErrors.unitNo = "Unit No required";
    if (!formData.name || !formData.name.trim()) newErrors.name = "NAME is required";
    if (!formData.memberName || !formData.memberName.trim()) newErrors.memberName = "memberName is required";
    if (!formData.designation) newErrors.designation = "designation is required";
    if (!formData.startDate) newErrors.startDate = "start Date is required";
    if (!formData.agree) newErrors.agree = "You must agree before submitting";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUnitSelect = (unit) => {
    setFormData((prev) => ({
      ...prev,
      unitNo: unit.value,
      memberName: unit.label,
      wing: unit.wing,
      name: prev.name || unit.label,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setPdfFile(null);
      return;
    }
    if (file.type === "application/pdf") {
      setPdfFile(file);
      setErrors((prev) => ({ ...prev, pdfFile: undefined }));
    } else {
      toast.error("Please select a PDF file", {
        position: "bottom-right",
        autoClose: 3000,
      });
      setPdfFile(null);
    }
  };

  useEffect(() => {
    if (!formData.unitNo) {
      setFormData((prev) => ({ ...prev, memberName: "", unitNo: "" }));
      return;
    }
    const match = allUnitNumber.find((item) => String(item.unit_no) === String(formData.unitNo));

    if (match && match.member_name) {
      setFormData((prev) => {
        const shouldCopyName = !prev.name || prev.name.trim() === "";
        return {
          ...prev,
          memberName: match.member_name,
          name: shouldCopyName ? match.member_name : prev.name,
          wing: match.wing || "",
        };
      });
    }
  }, [formData.unitNo, allUnitNumber]);

  const normalized = (s) => (s || "").trim().toLowerCase();
  const pdfDisabled = normalized(formData.name) === normalized(formData.memberName) || normalized(formData.name) === "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("You must be logged in to perform this action.", {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const form = new FormData();
      form.append("unitNo", String(formData.unitNo));
      form.append("name", formData.name ? formData.name.trim() : "");
      form.append("memberName", formData.memberName ? formData.memberName.trim() : "");
      form.append("designation", formData.designation ? formData.designation.trim() : "");
      form.append("startDate", formData.startDate ? formData.startDate : "");
      if (pdfFile && !pdfDisabled) form.append("file", pdfFile);
      form.append("agree", formData.agree ? "true" : "false");
      form.append("societyName", societyName);

      const response = await axios.post(`${BASE_URL}/api/assign-role/add-assign-role`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Assign role added successfully", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } catch (err) {
      toast.error("Error adding Assign role. Check console for details.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const society = societyName;

        if (!token) {
          setError("Not authenticated");
          setLoading(false);
          return;
        }

        const response = await axios.get(`${BASE_URL}/api/member/get-list?society=${encodeURIComponent(society)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!cancelled) {
          setAllUnitNumber(response.data || []);
          setLoading(false);
        }
      } catch (err) {
        if (err.name === "CanceledError" || err.message === "canceled") {
          return;
        }
        console.error("Error fetching data:", err);
        setError("Failed to fetch data. Make sure you are logged in.");
        setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [societyName]);

  return (
    <div className="create-assign-wrapper-modern">
      <ToastContainer position="top-right" autoClose={3000} />
      <form className="create-assign-form-modern" onSubmit={handleSubmit}>
        <div className="form-grid-assign">
          <div className="form-field-assign">
            <label className="form-label-assign">
              <Home size={16} />
              <span>Unit No</span>
              <span className="required">*</span>
            </label>
            <SearchableUnitSelect
              allUnitNumber={allUnitNumber}
              value={formData.unitNo}
              onChange={handleUnitSelect}
              placeholder="Start typing unit to filter..."
            />
            {errors.unitNo && <div className="error-message-assign">{errors.unitNo}</div>}
          </div>

          <div className="form-field-assign">
            <label className="form-label-assign">
              <User size={16} />
              <span>Name</span>
              <span className="required">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-input-assign"
              placeholder="Enter name"
            />
            {errors.name && <div className="error-message-assign">{errors.name}</div>}
            <div className="field-hint">You can edit this</div>
          </div>

          <div className="form-field-assign">
            <label className="form-label-assign">
              <Home size={16} />
              <span>Wing / Unit</span>
              <span className="required">*</span>
            </label>
            <div className="wing-input-group">
              <input
                type="text"
                name="unitNo"
                value={formData.unitNo}
                onChange={handleChange}
                className="form-input-assign"
                placeholder="Unit number"
              />
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  name="agree"
                  id="agree-checkbox"
                  checked={formData.agree}
                  onChange={handleChange}
                  className="checkbox-input"
                />
                <label htmlFor="agree-checkbox" className="checkbox-label">
                  <CheckCircle2 size={16} />
                  <span>I confirm</span>
                </label>
              </div>
            </div>
            {errors.wing && <div className="error-message-assign">{errors.wing}</div>}
            {errors.agree && <div className="error-message-assign">{errors.agree}</div>}
            <div className="field-hint">You can edit this</div>
          </div>

          <div className="form-field-assign">
            <label className="form-label-assign">
              <Users size={16} />
              <span>Member Name</span>
              <span className="required">*</span>
            </label>
            <input
              type="text"
              name="memberName"
              value={formData.memberName}
              onChange={handleChange}
              readOnly
              className="form-input-assign readonly"
              placeholder="Auto-filled from unit"
            />
            {errors.memberName && <div className="error-message-assign">{errors.memberName}</div>}
          </div>

          <div className="form-field-assign">
            <label className="form-label-assign">
              <Briefcase size={16} />
              <span>Designation</span>
              <span className="required">*</span>
            </label>
            <select
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              className="form-select-assign"
            >
              <option value="">Select Designation</option>
              <option value="CHAIRMAN">CHAIRMAN</option>
              <option value="SECRETARY">SECRETARY</option>
              <option value="TREASURER">TREASURER</option>
              <option value="COMMITEE MEMBER">COMMITTEE MEMBER</option>
            </select>
            {errors.designation && <div className="error-message-assign">{errors.designation}</div>}
          </div>

          <div className="form-field-assign">
            <label className="form-label-assign">
              <Calendar size={16} />
              <span>Start Date</span>
              <span className="required">*</span>
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              className="form-input-assign"
            />
            {errors.startDate && <div className="error-message-assign">{errors.startDate}</div>}
          </div>

          <div className="form-field-assign full-width-assign">
            <label className="form-label-assign">
              <FileUp size={16} />
              <span>Upload NOC (PDF)</span>
            </label>

            {pdfDisabled ? (
              <>
                <input
                  type="file"
                  accept="application/pdf"
                  disabled
                  className="file-input-assign disabled"
                />
                <div className="field-hint warning">
                  PDF upload disabled because Name and Member Name are identical. Edit the Name to enable upload.
                </div>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="file-input-assign"
                />
                <div className="field-hint">
                  If someone else will act on your behalf, a signed NOC from the registered flat owner along with ID proofs is mandatory. Without it the society will not process requests on behalf of the owner.
                </div>
                {errors.pdfFile && <div className="error-message-assign">{errors.pdfFile}</div>}
                {pdfFile && <div className="file-selected">{pdfFile.name}</div>}
              </>
            )}
          </div>
        </div>

        <div className="form-actions-assign">
          <button type="submit" className="submit-btn-assign" disabled={loading}>
            <Save size={18} />
            <span>{loading ? "Submitting..." : "Assign Role"}</span>
          </button>
        </div>
        {error && <div className="error-message-assign global-error">{error}</div>}
      </form>
    </div>
  );
};

export default CreateAssignRole;
